import { z } from 'zod';
import { capturePostHogEvent } from './analytics';
import { isHostedHostname } from './apiBaseUrl';

const MINUTE = 60_000;
const CHECK_INTERVAL = 10 * MINUTE;
const RETURN_CHECK_INTERVAL = 2 * MINUTE;
const RELOAD_GUARD_KEY = 'app_stale_build_reload';
const versionSchema = z.object({
  gitSha: z.string().trim().min(1),
  builtAt: z.string().trim().min(1),
});
const reloadGuardSchema = z.object({
  fromSha: z.string(),
  toSha: z.string(),
  at: z.number().finite(),
});

export function startStaleBuildMonitor() {
  if (process.env.NODE_ENV === 'test' || !isHostedHostname(window.location.hostname)) return;

  const startedAt = Date.now();
  let baseline: z.infer<typeof versionSchema> | undefined;
  let latest: z.infer<typeof versionSchema> | undefined;
  let pathname = new URL(window.location.href).pathname;
  let lastCheck = -Infinity;
  let inFlight: AbortController | undefined;
  let nextCheckTimer: number | undefined;
  let timeoutTimer: number | undefined;
  let stopped = false;
  let reloaded = false;

  function dispose() {
    stopped = true;
    window.clearTimeout(nextCheckTimer);
    window.clearTimeout(timeoutTimer);
    inFlight?.abort();
    window.removeEventListener('focus', onReturn);
    document.removeEventListener('visibilitychange', onReturn);
  }

  async function check(minimumAge: number) {
    if (stopped || inFlight || Date.now() - lastCheck < minimumAge) return;
    lastCheck = Date.now();
    window.clearTimeout(nextCheckTimer);
    nextCheckTimer = window.setTimeout(() => void check(CHECK_INTERVAL), CHECK_INTERVAL);
    const controller = new AbortController();
    inFlight = controller;
    timeoutTimer = window.setTimeout(() => controller.abort(), 10_000);
    try {
      const response = await fetch('/version.json', {
        cache: 'no-store',
        signal: controller.signal,
      });
      if (response.status !== 200) throw new Error('Version unavailable');
      const version = versionSchema.parse(await response.json());
      if (stopped || controller.signal.aborted) return;
      // The release workflow rewrites version.json independently of the baked SHA.
      baseline ??= version;
      latest = version;
    } catch {
      // A host without a boot version cannot safely participate in detection.
      if (!baseline) dispose();
    } finally {
      window.clearTimeout(timeoutTimer);
      inFlight = undefined;
    }
  }

  function onReturn() {
    if (document.visibilityState === 'visible') void check(RETURN_CHECK_INTERVAL);
  }

  function navigate(fullUrl: string) {
    const destination = new URL(fullUrl, window.location.href);
    const fromPathname = pathname;
    pathname = destination.pathname;
    if (
      stopped ||
      reloaded ||
      fromPathname === pathname ||
      !baseline ||
      !latest ||
      (baseline.gitSha === latest.gitSha && baseline.builtAt === latest.builtAt)
    )
      return;

    try {
      const previous = reloadGuardSchema.safeParse(
        JSON.parse(sessionStorage.getItem(RELOAD_GUARD_KEY) || 'null'),
      );
      if (
        previous.success &&
        previous.data.toSha === latest.gitSha &&
        Date.now() - previous.data.at < 5 * MINUTE
      )
        return;
      sessionStorage.setItem(
        RELOAD_GUARD_KEY,
        JSON.stringify({
          fromSha: baseline.gitSha,
          toSha: latest.gitSha,
          at: Date.now(),
        }),
      );
    } catch {
      // Without a persistent guard, another document load could loop indefinitely.
      return;
    }

    reloaded = true;
    // oxlint-disable-next-line anti-slop/no-runtime-typeof -- Older browsers may lack the Beacon API.
    const supportsBeacon = typeof navigator.sendBeacon === 'function';
    capturePostHogEvent(
      'app_stale_build_reloaded',
      {
        reason: 'version_changed',
        boot_sha: baseline.gitSha,
        latest_sha: latest.gitSha,
        boot_built_at: baseline.builtAt,
        latest_built_at: latest.builtAt,
        tab_age_minutes: (Date.now() - startedAt) / MINUTE,
        from_pathname: fromPathname,
        to_pathname: pathname,
        // Override URL defaults from both the helper and the PostHog SDK.
        page_location: pathname,
        $current_url: pathname,
        page_referrer: fromPathname,
        $referrer: fromPathname,
      },
      supportsBeacon ? { transport: 'sendBeacon' } : {},
    );
    window.location.assign(destination.href);
  }

  window.addEventListener('focus', onReturn);
  document.addEventListener('visibilitychange', onReturn);
  void check(0);
  return { navigate, dispose };
}
